import { Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { sum } from 'lodash-es';
import React from 'react';
import NumberText from '~/components/NumberText';
import { GreenBillingCalculation } from '~/models/retailer';
import { Section, SectionContainer, StyledTableCell, StyledTableRow } from './style';

export interface BillData {
  hasDiffValue?: boolean;
  lwaMLF: number;
  lwaDLF: number;
  lossFactor: number;
  greenBilling?: GreenBillingCalculation;
  peakEnergy: number;
  offPeakEnergy: number;
  shoulderEnergy: number;
  totalEnergy: number;
  /** When numOfMeters > 1, use pre-computed per-meter sum for quantity inc loss */
  peakEnergyIncLoss?: number;
  offPeakEnergyIncLoss?: number;
  shoulderEnergyIncLoss?: number;
  totalEnergyIncLoss?: number;
  contestableCost: number;
  energyCharge: {
    isTariff: boolean;
    peakRate: number;
    peakSubTotal: number;
    offPeakRate: number;
    offPeakSubTotal: number;
    shoulderRate: number;
    shoulderSubTotal: number;
    subTotal: number;
    diff?: {
      peak: number;
      offPeak: number;
      shoulder: number;
      subTotal: number;
    };
    diffInPercent?: {
      peak: number;
      offPeak: number;
      shoulder: number;
      subTotal: number;
    };
  };
  networkCharge: {
    standing: number;
    energy: number;
    demand: number;
  };
  regulatedCharge: {
    participantRate: number;
    ancillaryRate: number;
    subTotal: number;
    /** When set, row amount uses this (segment-sum aligned); otherwise rate × quantity */
    participantSubTotal?: number;
    ancillarySubTotal?: number;
  };
  environmentalCharge: {
    lrecRate: number;
    lrecSubTotal: number;
    sresRate: number;
    sresSubTotal: number;
    veetRate: number;
    veetSubTotal: number;
    essRate: number;
    essSubTotal: number;
    aeeisRate: number;
    aeeisSubTotal: number;
    pdrsRate: number;
    pdrsSubTotal: number;
    repsRate: number;
    repsSubTotal: number;
    subTotal: number;
    diff?: {
      lrec: number;
      sres: number;
      veet: number;
      ess: number;
      aeeis: number;
      pdrs: number;
      reps: number;
      subTotal: number;
    };
    diffInPercent?: {
      lrec: number;
      sres: number;
      veet: number;
      ess: number;
      aeeis: number;
      pdrs: number;
      reps: number;
      subTotal: number;
    };
  };
  meteringAndRetailerCharge: {
    numOfMeters: number;
    meteringChargeRate: number;
    meteringCharge: number;
    retailerFeeRate: number;
    retailerFee: number;
    subTotal: number;
    diff?: {
      meteringCharge: number;
      retailerFee: number;
      subTotal: number;
    };
    diffInPercent?: {
      meteringCharge: number;
      retailerFee: number;
      subTotal: number;
    };
  };
  diff?: number;
  diffInPercent?: number;
}

const DiffCharge: React.FC<{ diffValue?: number; diffValueInPercent?: number }> = ({
  diffValue,
  diffValueInPercent
}) => (
  <>
    <StyledTableCell
      sx={{
        bgcolor: (diffValue ?? 0) <= 0 ? '#a5d6a7' : '#ef9a9a',
        color: (diffValue ?? 0) <= 0 ? '#1b5e20' : '#c62828'
      }}
    >
      $<NumberText value={diffValue} precision={0} na="0" />
    </StyledTableCell>
    <StyledTableCell
      sx={{
        bgcolor: (diffValue ?? 0) <= 0 ? '#a5d6a7' : '#ef9a9a',
        color: (diffValue ?? 0) <= 0 ? '#1b5e20' : '#c62828'
      }}
    >
      <NumberText value={(diffValueInPercent ?? 0) * 100} precision={2} na="-" />%
    </StyledTableCell>
  </>
);

const BillSection: React.FC<{ data: BillData }> = ({ data }) => {
  const numOfMeters = data.meteringAndRetailerCharge.numOfMeters;
  const hideLossFactorColumns = numOfMeters > 1;
  // Environmental/Regulated: when multiple meters use pre-computed per-meter sum
  const totalEnergyIncLoss = data.totalEnergyIncLoss ?? data.totalEnergy * data.lossFactor;

  // Regulated: ensure row amounts add up to subTotal when subTotal is provided (avoid rate×qty when row sum would differ from total)
  const rc = data.regulatedCharge;
  const regulatedRateSum = (rc.participantRate ?? 0) + (rc.ancillaryRate ?? 0);
  const regulatedParticipantAmount =
    rc.participantSubTotal ??
    (rc.subTotal > 0 && regulatedRateSum > 0
      ? rc.subTotal * ((rc.participantRate ?? 0) / regulatedRateSum)
      : rc.subTotal > 0
      ? (rc.participantRate * totalEnergyIncLoss) / 100
      : 0);
  const regulatedAncillaryAmount =
    rc.ancillarySubTotal ??
    (rc.subTotal > 0 && regulatedRateSum > 0
      ? rc.subTotal * ((rc.ancillaryRate ?? 0) / regulatedRateSum)
      : rc.subTotal > 0
      ? (rc.ancillaryRate * totalEnergyIncLoss) / 100
      : 0);

  return (
    <Section>
      <SectionContainer>
        <TableContainer sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell colSpan={data.hasDiffValue === true ? 8 : 6} sx={{ textAlign: 'left !important' }}>
                  Energy Charges
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'brand.main' }}>
                <StyledTableCell sx={{ color: 'common.white' }}>Energy Charges</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Quantity Unit</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>
                  {hideLossFactorColumns ? 'Rate Unit (weighted avg)' : 'Rate Unit'}
                </StyledTableCell>
                {!hideLossFactorColumns && (
                  <>
                    <StyledTableCell sx={{ color: 'common.white' }}>MLF</StyledTableCell>
                    <StyledTableCell sx={{ color: 'common.white' }}>DLF</StyledTableCell>
                  </>
                )}
                <StyledTableCell sx={{ color: 'common.white' }}>Amount$(Ex GST)</StyledTableCell>
                {data.hasDiffValue === true && (
                  <>
                    <StyledTableCell sx={{ color: 'common.white' }}>$ Change</StyledTableCell>
                    <StyledTableCell sx={{ color: 'common.white' }}>% Change</StyledTableCell>
                  </>
                )}
              </TableRow>
              {data.energyCharge.peakRate > 0 && (
                <StyledTableRow>
                  <StyledTableCell>Peak</StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.peakEnergy} precision={0} /> kWh
                  </StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.energyCharge.peakRate} precision={4} /> c/kWh
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell>
                        <NumberText value={data.lwaMLF} precision={5} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <NumberText value={data.lwaDLF} precision={5} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    $<NumberText value={data.energyCharge.peakSubTotal} precision={0} />
                  </StyledTableCell>
                  {data.hasDiffValue === true && (
                    <DiffCharge
                      diffValue={data.energyCharge.diff?.peak}
                      diffValueInPercent={data.energyCharge.diffInPercent?.peak}
                    />
                  )}
                </StyledTableRow>
              )}

              {data.energyCharge.shoulderRate > 0 && (
                <StyledTableRow>
                  <StyledTableCell>Shoulder</StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.shoulderEnergy} precision={0} /> kWh
                  </StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.energyCharge.shoulderRate} precision={4} na="-" /> c/kWh
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell>
                        <NumberText value={data.lwaMLF} precision={5} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <NumberText value={data.lwaDLF} precision={5} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    $<NumberText value={data.energyCharge.shoulderSubTotal} precision={0} />
                  </StyledTableCell>
                  {data.hasDiffValue === true && (
                    <DiffCharge
                      diffValue={data.energyCharge.diff?.shoulder}
                      diffValueInPercent={data.energyCharge.diffInPercent?.shoulder}
                    />
                  )}
                </StyledTableRow>
              )}

              {data.energyCharge.offPeakRate > 0 && (
                <StyledTableRow>
                  <StyledTableCell>Off-Peak</StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.offPeakEnergy} precision={0} /> kWh
                  </StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.energyCharge.offPeakRate} precision={4} /> c/kWh
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell>
                        <NumberText value={data.lwaMLF} precision={5} />
                      </StyledTableCell>
                      <StyledTableCell>
                        <NumberText value={data.lwaDLF} precision={5} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    $<NumberText value={data.energyCharge.offPeakSubTotal} precision={0} />
                  </StyledTableCell>
                  {data.hasDiffValue === true && (
                    <DiffCharge
                      diffValue={data.energyCharge.diff?.offPeak}
                      diffValueInPercent={data.energyCharge.diffInPercent?.offPeak}
                    />
                  )}
                </StyledTableRow>
              )}

              <StyledTableRow sx={{ background: '#616161 !important' }}>
                <StyledTableCell sx={{ color: 'common.white' }}>Total Energy Charges</StyledTableCell>
                <StyledTableCell colSpan={hideLossFactorColumns ? 2 : 4}></StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>
                  $<NumberText value={data.energyCharge.subTotal} precision={0} />
                </StyledTableCell>
                {data.hasDiffValue === true && (
                  <DiffCharge
                    diffValue={data.energyCharge.diff?.subTotal}
                    diffValueInPercent={data.energyCharge.diffInPercent?.subTotal}
                  />
                )}
              </StyledTableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {sum([data.networkCharge.standing, data.networkCharge.energy, data.networkCharge.demand]) > 0 && (
          <TableContainer sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={6} sx={{ textAlign: 'left !important' }}>
                    Network Charges
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: 'brand.main' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Network Charges</StyledTableCell>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>Amount$(Ex GST)</StyledTableCell>
                </TableRow>
                <StyledTableRow>
                  <StyledTableCell>Network - Standing</StyledTableCell>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell>
                    $
                    <NumberText value={data.networkCharge.standing} precision={0} na="-" />
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>Network - Energy</StyledTableCell>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell>
                    $
                    <NumberText value={data.networkCharge.energy} precision={0} na="-" />
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>Network - Demand</StyledTableCell>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell>
                    $
                    <NumberText value={data.networkCharge.demand} precision={0} na="-" />
                  </StyledTableCell>
                </StyledTableRow>
                <TableRow sx={{ bgcolor: 'grey.A700' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Total Network Charges</StyledTableCell>
                  <StyledTableCell colSpan={4}></StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>
                    $
                    <NumberText
                      value={sum([data.networkCharge.standing, data.networkCharge.energy, data.networkCharge.demand])}
                      precision={0}
                    />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data.regulatedCharge.subTotal > 0 && (
          <TableContainer sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={6} sx={{ textAlign: 'left !important' }}>
                    Regulated Charges
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: 'brand.main' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Regulated Charges</StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>Quantity Unit</StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>
                    {hideLossFactorColumns ? 'Rate Unit (weighted avg)' : 'Rate Unit'}
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell sx={{ color: 'common.white' }}></StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>Loss Factor</StyledTableCell>
                    </>
                  )}
                  <StyledTableCell sx={{ color: 'common.white' }}>Amount$(Ex GST)</StyledTableCell>
                </TableRow>
                <StyledTableRow>
                  <StyledTableCell>AEMO Participant Charge</StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.totalEnergy} precision={0} /> kWh
                  </StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.regulatedCharge.participantRate} precision={4} /> c/kWh
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <NumberText value={data.lossFactor} precision={5} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    $<NumberText value={regulatedParticipantAmount} precision={0} />
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell>AEMO Ancillary Charge</StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.totalEnergy} precision={0} /> kWh
                  </StyledTableCell>
                  <StyledTableCell>
                    <NumberText value={data.regulatedCharge.ancillaryRate} precision={4} /> c/kWh
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell></StyledTableCell>
                      <StyledTableCell>
                        <NumberText value={data.lossFactor} precision={5} />
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell>
                    $<NumberText value={regulatedAncillaryAmount} precision={0} />
                  </StyledTableCell>
                </StyledTableRow>
                <TableRow sx={{ bgcolor: 'grey.A700' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Total Regulated Charges</StyledTableCell>
                  <StyledTableCell colSpan={hideLossFactorColumns ? 2 : 4}></StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>
                    $<NumberText value={data.regulatedCharge.subTotal} precision={0} />
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {data.environmentalCharge.subTotal > 0 && (
          <TableContainer sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell colSpan={data.hasDiffValue === true ? 8 : 6} sx={{ textAlign: 'left !important' }}>
                    Environmental Charges
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: 'brand.main' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Environmental Charges</StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>Quantity Unit</StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>
                    {hideLossFactorColumns ? 'Rate Unit (weighted avg)' : 'Rate Unit'}
                  </StyledTableCell>
                  {!hideLossFactorColumns && (
                    <>
                      <StyledTableCell sx={{ color: 'common.white' }}></StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>
                        {data.greenBilling === GreenBillingCalculation.DLF_MLF
                          ? 'DLF x MLF'
                          : data.greenBilling === GreenBillingCalculation.MLF
                          ? 'MLF'
                          : 'DLF'}
                      </StyledTableCell>
                    </>
                  )}
                  <StyledTableCell sx={{ color: 'common.white' }}>Amount$(Ex GST)</StyledTableCell>
                  {data.hasDiffValue === true && (
                    <>
                      <StyledTableCell sx={{ color: 'common.white' }}>$ Change</StyledTableCell>
                      <StyledTableCell sx={{ color: 'common.white' }}>% Change</StyledTableCell>
                    </>
                  )}
                </TableRow>
                {(data.environmentalCharge.lrecRate > 0 || data.environmentalCharge.lrecSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>LREC Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.lrecRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.lrecSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.lrec}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.lrec}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.sresRate > 0 || data.environmentalCharge.sresSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>SREC Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.sresRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.sresSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.sres}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.sres}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.veetRate > 0 || data.environmentalCharge.veetSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>VEEC Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.veetRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.veetSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.veet}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.veet}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.essRate > 0 || data.environmentalCharge.essSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>ESS Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.essRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.essSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.ess}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.ess}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.aeeisRate > 0 || data.environmentalCharge.aeeisSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>AEEIS Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.aeeisRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.aeeisSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.aeeis}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.aeeis}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.pdrsRate > 0 || data.environmentalCharge.pdrsSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>PDRS Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.pdrsRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.pdrsSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.pdrs}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.pdrs}
                      />
                    )}
                  </StyledTableRow>
                )}

                {(data.environmentalCharge.repsRate > 0 || data.environmentalCharge.repsSubTotal > 0) && (
                  <StyledTableRow>
                    <StyledTableCell>REPS Charge</StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.totalEnergy} precision={0} /> kWh
                    </StyledTableCell>
                    <StyledTableCell>
                      <NumberText value={data.environmentalCharge.repsRate} precision={4} /> c/kWh
                    </StyledTableCell>
                    {!hideLossFactorColumns && (
                      <>
                        <StyledTableCell></StyledTableCell>
                        <StyledTableCell>
                          <NumberText value={data.lossFactor} precision={5} />
                        </StyledTableCell>
                      </>
                    )}
                    <StyledTableCell>
                      $<NumberText value={data.environmentalCharge.repsSubTotal} precision={0} />
                    </StyledTableCell>
                    {data.hasDiffValue === true && (
                      <DiffCharge
                        diffValue={data.environmentalCharge.diff?.reps}
                        diffValueInPercent={data.environmentalCharge.diffInPercent?.reps}
                      />
                    )}
                  </StyledTableRow>
                )}

                <TableRow sx={{ bgcolor: 'grey.A700' }}>
                  <StyledTableCell sx={{ color: 'common.white' }}>Total Environmental Charges</StyledTableCell>
                  <StyledTableCell colSpan={hideLossFactorColumns ? 2 : 4}></StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white' }}>
                    $<NumberText value={data.environmentalCharge.subTotal} precision={0} />
                  </StyledTableCell>
                  {data.hasDiffValue === true && (
                    <DiffCharge
                      diffValue={data.environmentalCharge.diff?.subTotal ?? 0}
                      diffValueInPercent={data.environmentalCharge.diffInPercent?.subTotal}
                    />
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TableContainer sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell colSpan={data.hasDiffValue === true ? 8 : 6} sx={{ textAlign: 'left !important' }}>
                  Metering And Retailer Charges
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'brand.main' }}>
                <StyledTableCell sx={{ color: 'common.white' }}>Metering And Retailer Charges</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Quantity Unit</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Rate</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Quantity</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Unit</StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>Amount$(Ex GST)</StyledTableCell>
                {data.hasDiffValue === true && (
                  <>
                    <StyledTableCell sx={{ color: 'common.white' }}>$ Change</StyledTableCell>
                    <StyledTableCell sx={{ color: 'common.white' }}>% Change</StyledTableCell>
                  </>
                )}
              </TableRow>
              <StyledTableRow>
                <StyledTableCell>Metering Charge</StyledTableCell>
                <StyledTableCell>{data.meteringAndRetailerCharge.numOfMeters} Meter(s)</StyledTableCell>
                <StyledTableCell>
                  $<NumberText value={data.meteringAndRetailerCharge.meteringChargeRate} precision={2} />
                </StyledTableCell>
                <StyledTableCell>1</StyledTableCell>
                <StyledTableCell>Years</StyledTableCell>
                <StyledTableCell>
                  $<NumberText value={data.meteringAndRetailerCharge.meteringCharge} precision={0} />
                </StyledTableCell>
                {data.hasDiffValue === true && (
                  <DiffCharge
                    diffValue={data.meteringAndRetailerCharge.diff?.meteringCharge}
                    diffValueInPercent={data.meteringAndRetailerCharge.diffInPercent?.meteringCharge}
                  />
                )}
              </StyledTableRow>
              <StyledTableRow>
                <StyledTableCell>Retailer Fee</StyledTableCell>
                <StyledTableCell>{data.meteringAndRetailerCharge.numOfMeters} Meter(s)</StyledTableCell>
                <StyledTableCell>
                  $<NumberText value={data.meteringAndRetailerCharge.retailerFeeRate} precision={2} />
                </StyledTableCell>
                <StyledTableCell>1</StyledTableCell>
                <StyledTableCell>Years</StyledTableCell>
                <StyledTableCell>
                  $<NumberText value={data.meteringAndRetailerCharge.retailerFee} precision={0} />
                </StyledTableCell>
                {data.hasDiffValue === true && (
                  <DiffCharge
                    diffValue={data.meteringAndRetailerCharge.diff?.retailerFee}
                    diffValueInPercent={data.meteringAndRetailerCharge.diffInPercent?.retailerFee}
                  />
                )}
              </StyledTableRow>
              <TableRow sx={{ bgcolor: 'grey.A700' }}>
                <StyledTableCell sx={{ color: 'common.white' }}>Total Metering And Retailer Charges</StyledTableCell>
                <StyledTableCell colSpan={4}></StyledTableCell>
                <StyledTableCell sx={{ color: 'common.white' }}>
                  $<NumberText value={data.meteringAndRetailerCharge.subTotal} precision={0} />
                </StyledTableCell>
                {data.hasDiffValue === true && (
                  <DiffCharge
                    diffValue={data.meteringAndRetailerCharge.diff?.subTotal ?? 0}
                    diffValueInPercent={data.meteringAndRetailerCharge.diffInPercent?.subTotal}
                  />
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <TableContainer sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell colSpan={6} sx={{ textAlign: 'left !important' }}>
                  TOTAL CHARGES
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ bgcolor: 'brand.main' }}>
                <StyledTableCell sx={{ textAlign: 'left !important', fontWeight: 'bold' }}>Total</StyledTableCell>
                <StyledTableCell sx={{ textAlign: 'right !important', fontWeight: 'bold' }}>
                  $
                  <NumberText
                    value={sum([
                      data.contestableCost,
                      data.regulatedCharge.subTotal,
                      data.networkCharge.standing,
                      data.networkCharge.energy,
                      data.networkCharge.demand
                    ])}
                    precision={0}
                  />
                </StyledTableCell>
              </TableRow>
              {data.hasDiffValue === true && (
                <>
                  <TableRow
                    sx={{
                      bgcolor: (data.diff ?? 0) <= 0 ? '#a5d6a7' : '#ef9a9a',
                      color: (data.diff ?? 0) <= 0 ? '#1b5e20' : '#c62828'
                    }}
                  >
                    <StyledTableCell sx={{ textAlign: 'left !important', fontWeight: 'bold' }}>
                      $ Charge
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: 'right !important', fontWeight: 'bold' }}>
                      $<NumberText value={data.diff ?? 0} precision={0} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow
                    sx={{
                      bgcolor: (data.diff ?? 0) <= 0 ? '#a5d6a7' : '#ef9a9a',
                      color: (data.diff ?? 0) <= 0 ? '#1b5e20' : '#c62828'
                    }}
                  >
                    <StyledTableCell sx={{ textAlign: 'left !important', fontWeight: 'bold' }}>
                      % Charge
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: 'right !important', fontWeight: 'bold' }}>
                      <NumberText value={(data.diffInPercent ?? 0) * 100} precision={0} />%
                    </StyledTableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionContainer>
    </Section>
  );
};

export default BillSection;
